import { Plugin } from 'ckeditor5/src/core';
import { Widget, toWidget, toWidgetEditable } from 'ckeditor5/src/widget';
import InsertAccordionCommand from './accordioncommand';
import { generateAccordionId, getAccordionState } from './utils';
import { enablePlaceholder } from 'ckeditor5/src/engine';

export default class AccordionEditing extends Plugin {
    static get requires() {
        return [Widget];
    }

    init() {
        this._defineSchema();
        this._defineConverters();

        this.editor.commands.add(
			'insertAccordion',
			new InsertAccordionCommand(this.editor)
		);
    }

    _defineSchema() {
		const schema = this.editor.model.schema;

		schema.register('accordionWrapper', {
			// Behaves like a self-contained object (e.g. an image).
			isObject: true,

			// Allow in places where other blocks are allowed (e.g. directly in the root).
			allowWhere: '$block',
		});

		schema.register('accordion', {
			// Behaves like a self-contained object (e.g. an image).
			isObject: true,

			allowIn: 'accordionWrapper',

			allowAttributes: ['id'],
		});

		schema.register('accordionTitle', {
			// Cannot be split or left by the caret.
			isLimit: true,

			allowIn: 'accordion',

			// Allow content which is allowed in blocks (i.e. text with attributes).
			allowContentOf: '$block',
		});

		schema.register('accordionContent', {
			// Cannot be split or left by the caret.
			isLimit: true,

			allowIn: 'accordion',

			// Allow content which is allowed in the root (e.g. paragraphs).
			allowContentOf: '$root',
		});
	}

	_defineConverters() {
		const conversion = this.editor.conversion;
		const view = this.editor.editing.view;

		// <accordionWrapper> converters
		conversion.for('upcast').elementToElement({
			model: 'accordionWrapper',
			view: {
				name: 'div',
				classes: 'ck-accordion-wrapper',
			},
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'accordionWrapper',
			view: {
				name: 'div',
				classes: 'ck-accordion-wrapper',
			},
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'accordionWrapper',
			view: (modelElement, { writer: viewWriter }) => {
				const div = viewWriter.createContainerElement('div', {
					class: 'ck-accordion-wrapper',
				});

				return toWidget(div, viewWriter, {
					label: 'accordion widget',
					hasSelectionHandle: true,
				});
			},
		});

		// <accordion> converters
		conversion.for('upcast').elementToElement({
			model: (viewElement, { writer: modelWriter }) => {
				const accordionId =
					viewElement.getAttribute('data-id') ||
					generateAccordionId();
				return modelWriter.createElement('accordion', {
					id: accordionId,
				});
			},
			view: {
				name: 'details',
				classes: 'ck-accordion',
			},
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'accordion',
			view: (modelElement, { writer: viewWriter }) => {
				const accordionId = modelElement.getAttribute('id');
				const isOpen = getAccordionState(accordionId) === 'open';
				const detailsAttributes = {
					class: 'ck-accordion',
					'data-id': accordionId,
					'aria-expanded': isOpen ? 'true' : 'false',
				};
				if (isOpen) {
					detailsAttributes.open = 'true';
				}

				return viewWriter.createContainerElement('details', detailsAttributes);
			},
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'accordion',
			view: (modelElement, { writer: viewWriter }) => {
				const accordionId = modelElement.getAttribute('id');
				const isOpen = getAccordionState(accordionId) === 'open';
				const detailsAttributes = {
					class: 'ck-accordion',
					'data-id': accordionId,
					'aria-expanded': isOpen ? 'true' : 'false',
				};
				if (isOpen) {
					detailsAttributes.open = 'true';
				}

				const details = viewWriter.createContainerElement('details', detailsAttributes);
				return toWidgetEditable(details, viewWriter);
			},
		});

		// <accordionTitle> converters
		conversion.for('upcast').elementToElement({
			model: 'accordionTitle',
			view: {
				name: 'summary',
				classes: 'ck-accordion-title',
			},
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'accordionTitle',
			view: (modelElement, { writer: viewWriter }) => {
				const accordion = modelElement.parent; // Obtenemos el elemento padre (accordion).
				const accordionId = accordion.getAttribute('id'); // Obtenemos el id desde el accordion.

				const titleAttributes = {
					class: 'ck-accordion-title',
					'aria-controls': `${accordionId}-content`, // Vincula con el contenido.
				};

				return viewWriter.createEditableElement('summary', titleAttributes);
			},
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'accordionTitle',
			view: (modelElement, { writer: viewWriter }) => {
				const accordion = modelElement.parent; // Obtenemos el elemento padre (accordion).
				const accordionId = accordion.getAttribute('id'); // Obtenemos el id desde el accordion.

				const titleAttributes = {
					class: 'ck-accordion-title',
					'aria-controls': `${accordionId}-content`, // Vincula con el contenido.
				};

				const summary = viewWriter.createEditableElement('summary', titleAttributes);

				enablePlaceholder({
					view: this.editor.editing.view,
					element: summary,
					text: this.editor.t('Accordion title...'),
					keepOnFocus: true,
				});

				return toWidgetEditable(summary, viewWriter);
			},
		});

		// <accordionContent> converters
		conversion.for('upcast').elementToElement({
			model: 'accordionContent',
			view: {
				name: 'div',
				classes: 'ck-accordion-content',
			},
		});
		conversion.for('dataDowncast').elementToElement({
			model: 'accordionContent',
			view: (modelElement, { writer: viewWriter }) => {
				const accordion = modelElement.parent; // Obtenemos el elemento padre (accordion).
				const accordionId = accordion.getAttribute('id'); // Obtenemos el id desde el accordion.

				const contentAttributes = {
					class: 'ck-accordion-content',
					id: `${accordionId}-content`, // Asigna un ID único para relacionar con aria-labelledby.
					'aria-labelledby': `${accordionId}-title`, // Vincula con el título.
				};

				return viewWriter.createEditableElement('div', contentAttributes);
			},
		});
		conversion.for('editingDowncast').elementToElement({
			model: 'accordionContent',
			view: (modelElement, { writer: viewWriter }) => {
				const accordion = modelElement.parent; // Obtenemos el elemento padre (accordion).
				const accordionId = accordion.getAttribute('id'); // Obtenemos el id desde el accordion.

				const contentAttributes = {
					class: 'ck-accordion-content',
					id: `${accordionId}-content`, // Asigna un ID único para relacionar con aria-labelledby.
					'aria-labelledby': `${accordionId}-title`, // Vincula con el título.
				};

				const div = viewWriter.createEditableElement('div', contentAttributes);

				enablePlaceholder({
					view: this.editor.editing.view,
					element: div,
					text: this.editor.t('Empty accordion'),
					keepOnFocus: true,
				});

				return toWidgetEditable(div, viewWriter);
			},
		});
	}
}